/** 

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

import { useState, useEffect } from 'react';

// @mui material components
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

// Vision UI Dashboard PRO React components
import VuiBox from 'components/VuiBox';
import VuiTypography from 'components/VuiTypography';
import VuiButton from 'components/VuiButton';
import VuiInput from 'components/VuiInput';
import VuiSelect from 'components/VuiSelect';
import VuiPagination from 'components/VuiPagination';
import Icon from '@mui/material/Icon';

// Vision UI Dashboard PRO React example components
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';

// API and utilities
import { takedownsApi } from 'services/takedownsApi';
import { transformTakedownsData, filterTakedownsData } from 'utils/takedownsDataTransform';


function Takedowns() {
  // State management
  const [activeFilter, setActiveFilter] = useState(null);
  const [takedownsData, setTakedownsData] = useState({ columns: [], rows: [], pagination: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiParams, setApiParams] = useState({
    page: 1,
    limit: 20,
    sort_by: 'detected_date',
    sort_order: 'DESC'
  });
  const [availableFilters, setAvailableFilters] = useState({
    takedown_status: [],
    violation_type: [],
    media_type: []
  });
  const [counts, setCounts] = useState(null);

  // Generate filter buttons with counts
  const getFilterButtons = () => {
    const baseFilters = [
      { key: 'requiresApproval', label: 'Requires Approval', filter: { takedown_status: 'Pending' } },
      { key: 'Explicit', label: 'Explicit', filter: { violation_type: 'Explicit' } },
      { key: 'Copyright', label: 'Copyright', filter: { violation_type: 'Copyright' } }
    ];

    return baseFilters.map(filter => {
      let count = 0;
      if (counts?.breakdown) {
        if (filter.key === 'requiresApproval') {
          const statusBreakdown = counts.breakdown.by_status?.find(item => item.status === 'Pending');
          count = statusBreakdown?.count || 0;
        } else if (filter.key === 'Explicit') {
          const typeBreakdown = counts.breakdown.by_violation_type?.find(item => item.violation_type === 'Explicit');
          count = typeBreakdown?.count || 0;
        } else if (filter.key === 'Copyright') {
          const typeBreakdown = counts.breakdown.by_violation_type?.find(item => item.violation_type === 'Copyright');
          count = typeBreakdown?.count || 0;
        }
      }

      return {
        ...filter,
        label: count > 0 ? `${filter.label} (${count})` : filter.label
      };
    });
  };

  // Fetch data from API using optimized endpoint with parallel counts loading
  const fetchTakedownsData = async (newParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = { ...apiParams, ...newParams };
      
      // Extract filters for counts API
      const filters = {
        takedown_status: params.takedown_status,
        violation_type: params.violation_type,
        media_type: params.media_type,
        username: params.username,
        search: params.search
      };
      
      // Fetch both counts and data in parallel
      const [countsResponse, dataResponse] = await Promise.all([
        takedownsApi.fetchCounts(filters),
        takedownsApi.fetchOptimizedContent(params)
      ]);
      
      // Update counts
      setCounts(countsResponse);
      
      // Update available filters from response
      if (dataResponse.filters) {
        setAvailableFilters(dataResponse.filters);
      }
      
      // Update API parameters
      setApiParams(params);
      
      // Transform data
      const transformedData = transformTakedownsData(dataResponse);
      setTakedownsData(transformedData);
    } catch (err) {
      setError(`Failed to load takedowns data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch only counts (useful for filter changes)
  const fetchCountsOnly = async (filters = {}) => {
    try {
      const countsResponse = await takedownsApi.fetchCounts(filters);
      setCounts(countsResponse);
    } catch (err) {
    }
  };

  // Handle filter changes
  const handleFilterChange = (filterKey) => {
    setActiveFilter(filterKey);
    
    // Find the filter configuration
    const filterButtons = getFilterButtons();
    const selectedFilter = filterButtons.find(f => f.key === filterKey);
    
    if (selectedFilter) {
      // Reset to page 1 when filtering
      fetchTakedownsData({ ...selectedFilter.filter, page: 1 });
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    fetchTakedownsData({ page: newPage });
  };

  // Handle search with debouncing
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  const handleSearch = (value) => {
    setSearchTerm(value);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      fetchTakedownsData({ search: value, page: 1 });
    }, 500); // 500ms debounce
    
    setSearchTimeout(timeout);
  };

  // Handle entries per page change
  const handleEntriesPerPageChange = (newLimit) => {
    fetchTakedownsData({ limit: newLimit, page: 1 });
  };

  // Initial data fetch
  useEffect(() => {
    fetchTakedownsData();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3} sx={{ overflow: "visible" }}>
        {/* Header */}
        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <VuiBox>
            <VuiTypography variant="h3" color="white" fontWeight="bold">
              Takedowns Dashboard
            </VuiTypography>
            
          </VuiBox>
          {/* Pagination and Refresh Button 
          <VuiButton
            color="info"
            variant="outlined"
            onClick={() => fetchTakedownsData()}
            disabled={loading}
            sx={{
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </VuiButton>
          */}
        </VuiBox>

        {/* Filter Tabs */}
        <VuiBox mb={3} display="flex" gap={2} alignItems="center">
        <VuiBox width="300px">
                    <VuiInput
                      placeholder="Search Takedowns"
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          '&:hover': {
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                          },
                          '&.Mui-focused': {
                            border: '1px solid #0075FF',
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: 'white',
                          '&::placeholder': {
                            color: 'rgb(255, 255, 255)',
                          },
                        },
                      }}
                    />
                  </VuiBox>
          {getFilterButtons().map((filter) => (
            <VuiButton
              key={filter.key}
              color={activeFilter === filter.key ? 'info' : 'dark'}
              variant={activeFilter === filter.key ? 'contained' : 'outlined'}
              onClick={() => handleFilterChange(filter.key)}
              sx={{
                color: 'rgba(255, 255, 255, 0.75)',
                border: activeFilter !== filter.key ? '1px solid rgba(255, 255, 255, 0.5)' : 'none',
                '&:hover': {
                  backgroundColor:
                    activeFilter === filter.key ? '#0075FF' : 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              {filter.label}
            </VuiButton>
          ))}
          {activeFilter && (
            <VuiButton
              color="dark"
              variant="outlined"
              onClick={() => {
                setActiveFilter(null);
                // Clear all filters and reset to page 1
                fetchTakedownsData({ 
                  page: 1,
                  takedown_status: undefined,
                  violation_type: undefined,
                  media_type: undefined,
                  username: undefined,
                  search: undefined
                });
                // Clear search term
                setSearchTerm('');
              }}
              sx={{
                border: '1px solid rgba(255, 255, 255, 0.5)',
                color: 'rgba(255, 255, 255, 0.75)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              Clear Filter
            </VuiButton>
          )}
        </VuiBox>

        {/* Main Content Card */}
        <Card sx={{ overflow: "visible" }}>
          <VuiBox>
            {loading ? (
              <VuiBox display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress color="info" />
                <VuiTypography variant="button" color="text" ml={2}>
                  Loading takedowns data...
                </VuiTypography>
              </VuiBox>
            ) : error ? (
              <VuiBox p={3}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
                <VuiButton 
                  color="info" 
                  variant="contained" 
                  onClick={() => fetchTakedownsData()}
                >
                  Retry
                </VuiButton>
              </VuiBox>
            ) : (
              <VuiBox>
                {/* Search Input */}
                <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
                  
                 
                </VuiBox>

                {/* Data Table with server-side data */}
                <DataTable
                  table={takedownsData}
                  entriesPerPage={{
                    defaultValue: apiParams.limit,
                    entries: [10, 20, 50, 100],
                  }}
                  canSearch={false} // Disable built-in search
                  showTotalEntries={true}
                  isSorted={true}
                  noEndBorder={false}
                  pagination={{
                    variant: 'contained',
                    color: 'info',
                    currentPage: takedownsData.pagination?.current_page || 1,
                    totalPages: takedownsData.pagination?.total_pages || 1,
                    totalItems: takedownsData.pagination?.total_items || 0,
                  }}
                  onPageChange={handlePageChange}
                  onEntriesPerPageChange={handleEntriesPerPageChange}
                />

              </VuiBox>
            )}
          </VuiBox>
        </Card>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Takedowns;
